/**
 * Order Guards — duplicate prevention + slippage protection
 *
 * Pure functions that validate orders before submission.
 * No side effects, no API calls.
 *
 * @ai-context
 *   Role: Pre-submission validation gate. Prevents duplicate orders, slippage
 *         beyond tolerance, and oversized positions. Last line of defense
 *         before an order hits the API.
 *   Pattern: Pure guard functions returning GuardResult (ok/fail + reason).
 *            runAllGuards chains them — first failure wins.
 *   Purity: No side effects, no API calls.
 *
 * @ai-invariant checkSize converts cents→dollars for value limit check.
 *   Same YES/NO complement as orderMath: NO cost = (100 - priceCents)/100.
 *   Passing fractional (0..1) prices produces ~100x undercount.
 *
 * @ai-invariant runAllGuards returns the FIRST failing guard. Order matters:
 *   duplicate → slippage → size. A duplicate match masks slippage violations.
 *
 * @ai-upstream
 *   - No external module dependencies (leaf module)
 *
 * @ai-downstream
 *   - orderGuards.test.ts (17 cases)
 *   - (Currently no component imports — guard integration pending)
 */

export interface OrderFingerprint {
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  quantity: number;
  timestamp: number;
}

export interface GuardConfig {
  /** Window in ms to check for duplicates (default 5000) */
  duplicateWindowMs?: number;
  /** Max slippage in cents from expected price (default 3) */
  maxSlippageCents?: number;
  /** Max single order size in contracts (default 10000) */
  maxOrderSize?: number;
  /** Max single order value in dollars (default 50000) */
  maxOrderValueDollars?: number;
}

export type GuardResult =
  | { ok: true }
  | { ok: false; reason: string; code: 'duplicate' | 'slippage' | 'size' | 'value' };

const DEFAULT_CONFIG: Required<GuardConfig> = {
  duplicateWindowMs: 5000,
  maxSlippageCents: 3,
  maxOrderSize: 10000,
  maxOrderValueDollars: 50000,
};

/** Generate a fingerprint hash for dedup comparison */
export function fingerprint(order: OrderFingerprint): string {
  return `${order.ticker}|${order.side}|${order.priceCents}|${order.quantity}`;
}

/** Check for duplicate orders within time window */
export function checkDuplicate(
  order: OrderFingerprint,
  recentOrders: OrderFingerprint[],
  config?: GuardConfig
): GuardResult {
  const windowMs = config?.duplicateWindowMs ?? DEFAULT_CONFIG.duplicateWindowMs;
  const fp = fingerprint(order);
  const cutoff = order.timestamp - windowMs;

  const isDup = recentOrders.some(
    (recent) => recent.timestamp >= cutoff && fingerprint(recent) === fp
  );

  if (isDup) {
    return {
      ok: false,
      reason: `Duplicate order detected within ${windowMs / 1000}s window`,
      code: 'duplicate',
    };
  }
  return { ok: true };
}

/** Check slippage from expected price */
export function checkSlippage(
  actualPriceCents: number,
  expectedPriceCents: number,
  config?: GuardConfig
): GuardResult {
  const maxSlippage = config?.maxSlippageCents ?? DEFAULT_CONFIG.maxSlippageCents;
  const drift = Math.abs(actualPriceCents - expectedPriceCents);

  if (drift > maxSlippage) {
    return {
      ok: false,
      reason: `Price drifted ${drift}c from expected (max ${maxSlippage}c)`,
      code: 'slippage',
    };
  }
  return { ok: true };
}

/** Check order size limits */
export function checkSize(
  quantity: number,
  priceCents: number,
  side: 'yes' | 'no',
  config?: GuardConfig
): GuardResult {
  const maxSize = config?.maxOrderSize ?? DEFAULT_CONFIG.maxOrderSize;
  const maxValue = config?.maxOrderValueDollars ?? DEFAULT_CONFIG.maxOrderValueDollars;

  if (quantity > maxSize) {
    return {
      ok: false,
      reason: `Order size ${quantity} exceeds max ${maxSize} contracts`,
      code: 'size',
    };
  }

  // @ai-danger: Same YES/NO complement as orderMath. priceCents must be 0..100.
  //   costCents/100 → dollars per contract. Fractional input → 100x undercount.
  const costCents = side === 'yes' ? priceCents : 100 - priceCents;
  const valueDollars = (costCents / 100) * quantity;
  if (valueDollars > maxValue) {
    return {
      ok: false,
      reason: `Order value $${valueDollars.toFixed(2)} exceeds max $${maxValue.toLocaleString()}`,
      code: 'value',
    };
  }

  return { ok: true };
}

// @ai-note: Guard execution order: duplicate → slippage → size.
//   First failure short-circuits. Reordering changes which error the user sees.
/** Run all guards on an order. Returns first failure or ok. */
export function runAllGuards(
  order: OrderFingerprint,
  expectedPriceCents: number,
  recentOrders: OrderFingerprint[],
  config?: GuardConfig
): GuardResult {
  const dupCheck = checkDuplicate(order, recentOrders, config);
  if (!dupCheck.ok) return dupCheck;

  const slipCheck = checkSlippage(order.priceCents, expectedPriceCents, config);
  if (!slipCheck.ok) return slipCheck;

  const sizeCheck = checkSize(order.quantity, order.priceCents, order.side, config);
  if (!sizeCheck.ok) return sizeCheck;

  return { ok: true };
}

/**
 * @ai-modification-checklist
 *
 * Before modifying this file, verify:
 *
 * 1. UNIT CONTRACT — priceCents is 0..100. checkSize divides by 100 for
 *    dollar values. Same complement logic as orderMath.
 *
 * 2. GUARD ORDER — runAllGuards: duplicate → slippage → size. First failure
 *    wins. Reordering changes UX error messages.
 *
 * 3. DEFAULT THRESHOLDS — maxSlippageCents: 3, maxOrderSize: 10000,
 *    maxOrderValueDollars: 50000. Changes affect what orders get blocked.
 *
 * 4. TEST FILE — orderGuards.test.ts (17 cases).
 *    Run: npm run dashboard:test -- orderGuards
 */
